import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import queryString from "query-string";

import { getGinoSearchResultsRoute } from "../../../../../routers/routeUtils";

import { useSearchUsers } from "../../../../User/api/queries/useSearchUsers";

import { MessageContent } from "../../../types/openAI";
import { SeeAllIcon } from "../../../assets/icons/gino-icons";

import { UserCard, UserLoadingCard } from "../../UserCard";

import FormattedText from "./FormattedText";
import { useAllListsQuery } from "../../../../../api/queries/lists/useAllListsQuery";

const ListMessage = ({ json }: { json: Record<string, any> | null }) => {
  const { t, i18n } = useTranslation("gino", { keyPrefix: "component.listMessage" });

  const { message, ...rest } = json || {};

  const searchParams = {
    limit: 12,
    offset: 0,
    ...rest,
  };

  const navigate = useNavigate();
  const { data: searchResults, isLoading, isError } = useSearchUsers(searchParams);

  const formattedSearchParams = queryString.stringify(searchParams, { arrayFormat: "comma" });

  const handleSeeAll = () => {
    navigate(getGinoSearchResultsRoute(formattedSearchParams), { state: { searchParams } });
  };

  if ((!isLoading && searchResults?.items?.length === 0) || isError)
    return <Typography sx={{ width: "100%" }}>{t("noResults")}</Typography>;
  return (
    <Box sx={styles.listMessageContainer}>
      <Box sx={styles.listMessageHeader}>
        <FormattedText content={json?.message} />
        <Button
          variant="text-md"
          sx={styles.seeAllButton}
          endIcon={<SeeAllIcon fill={isLoading ? "#C2E6C4" : "#008B07"} />}
          disabled={isLoading}
          onClick={handleSeeAll}
        >
          {t("seeAll")}
        </Button>
      </Box>
      <Box sx={styles.resultsContainer}>
        {isLoading && Array.from({ length: 3 }).map((_, index) => <UserLoadingCard key={index} />)}
        {!isLoading && searchResults?.items?.map((user) => <UserCard key={user.id} user={user} />)}
      </Box>
    </Box>
  );
};

export default ListMessage;

const styles = {
  listMessageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  listMessageHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "80px",
  },
  seeAllButton: {
    flexShrink: 0,
    height: "fit-content",
  },
  resultsContainer: {
    display: "flex",
    gap: "12px",
    maxHeight: "100%",
    pb: "10px",
    overflowX: "auto",
  },
  userItem: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "var(--Light---100, #F5F5F5)",
    "&:hover": {
      backgroundColor: "var(--Light---200, #EEEEEE)",
    },
  },
};
